import React, { useState, useEffect } from 'react';
import { Table, message, Row, Col, Space, Button, Form, Input, Select, Popconfirm } from 'antd';
import { 
  DesktopOutlined, 
  ClusterOutlined, 
  EditOutlined, 
  PlusOutlined,
  DeleteOutlined,
  MinusCircleOutlined,
  DeploymentUnitOutlined,
  BoxPlotOutlined,
  EyeOutlined // Dodata ikonica
} from '@ant-design/icons';
import axios from 'axios';
import EditBoxModal from './EditBoxModal';

const { Option } = Select;

const MachineMappings = () => {
  const [boxes, setBoxes] = useState([]);
  const [machines, setMachines] = useState([]);
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // State za praćenje koji box ima upaljen mapping (npr. { box_id: true })
  const [activeMappings, setActiveMappings] = useState({});
  
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingBoxId, setEditingBoxId] = useState(null);
  const [form] = Form.useForm();

  const CARD_MIN_HEIGHT = '40px'; 
  const CARD_PADDING = '10px 20px'; 

  const BOX_URL = '/api/box/list/';
  const MACHINES_URL = '/api/box/machines-api/';
  const MAPPINGS_URL = '/api/box/mappings-api/';
  
  // Endpointi za paljenje i gašenje
  const SEND_MAPPING_URL = '/api/box/send-mapping/';
  const HIDE_MAPPING_URL = '/api/box/hide-mapping/';

  const fetchData = async () => {
    setLoading(true);
    try {
      const [resBoxes, resMachines, resMappings] = await Promise.all([
        axios.get(BOX_URL),
        axios.get(MACHINES_URL),
        axios.get(MAPPINGS_URL)
      ]);
      setBoxes(resBoxes.data.filter(b => b.id !== 294));
      setMachines(resMachines.data);
      setMappings(resMappings.data);
    } catch (error) {
      message.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // FUNKCIJA ZA TOGGLE (PALI/GASI)
  const toggleMapping = async (record) => {
    const boxId = record.box_id;
    const isCurrentlyActive = activeMappings[boxId];

    try {
      if (!isCurrentlyActive) {
        // PALI
        await axios.post(SEND_MAPPING_URL, { box_id: boxId });
        setActiveMappings(prev => ({ ...prev, [boxId]: true }));
        message.success(`Mapping shown on ${record.box_name}`);
      } else {
        // GASI
        await axios.post(HIDE_MAPPING_URL, { box_id: boxId });
        setActiveMappings(prev => ({ ...prev, [boxId]: false }));
        message.info(`Mapping hidden on ${record.box_name}`);
      }
    } catch (error) {
      message.error('Command failed to send');
    }
  };

  const openAddModal = () => {
    setEditingBoxId(null);
    form.resetFields();
    form.setFieldsValue({ rows: [{}] });
    setIsModalVisible(true);
  };

  const openFullEditModal = (boxRecord) => {
    setEditingBoxId(boxRecord.id);
    
    // Sortiramo mapiranja po broju mašine pre nego što ih ubacimo u formu
    const boxMappings = mappings
      .filter(m => m.box === boxRecord.id)
      .sort((a, b) => {
        const numA = parseInt(a.machine_display) || 0;
        const numB = parseInt(b.machine_display) || 0;
        return numA - numB;
      });
    
    form.setFieldsValue({
      box: boxRecord.id,
      rows: boxMappings.length > 0 ? boxMappings.map(m => ({
        id: m.id,
        machine: m.machine_display, 
        left_field: m.left_field,
        right_field: m.right_field
      })) : [{}]
    });
    setIsModalVisible(true);
  };

  const handleDeleteAllForBox = async (boxId) => {
    const boxMappings = mappings.filter(m => m.box === boxId);
    try {
      await Promise.all(boxMappings.map(m => axios.delete(`${MAPPINGS_URL}${m.id}/`)));
      message.success('All mappings removed');
      fetchData();
    } catch (error) {
      message.error('Error deleting');
    }
  };

  const handleSave = async (values) => {
    try {
      // Izmena ovde: Dozvoljava 0, ali preskače skroz prazna polja
      const validRows = values.rows.filter(r => 
        r.machine !== undefined && 
        (r.left_field !== undefined && r.left_field !== "") && 
        (r.right_field !== undefined && r.right_field !== "")
      );

      if (editingBoxId) {
        const oldMappings = mappings.filter(m => m.box === editingBoxId);
        await Promise.all(oldMappings.map(m => axios.delete(`${MAPPINGS_URL}${m.id}/`)));
      }
      
      await Promise.all(validRows.map(row => 
        axios.post(MAPPINGS_URL, {
          box: values.box,
          machine: row.machine,
          left_field: row.left_field,
          right_field: row.right_field
        })
      ));
      
      message.success('Saved');
      setIsModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('Error saving');
    }
  };

  const columns = [
    {
      title: 'BOX NAME (ID)',
      key: 'box',
      render: (record) => (
        <Space>
          <DesktopOutlined style={{ color: record.status === 'online' ? '#34d399' : '#64748b' }} />
          <div>
            <span style={{ color: 'white', fontWeight: 'bold' }}>{record.box_name || 'No Name'}</span>
            <span style={{ color: '#64748b', fontSize: '12px', marginLeft: '8px' }}>({record.box_id})</span>
          </div>
        </Space>
      )
    },
    {
      title: 'ASSIGNED MACHINES (FIELDS)',
      key: 'machine_info',
      render: (record) => {
        const boxMappings = mappings
          .filter(m => m.box === record.id)
          .sort((a, b) => Number(a.machine_display) - Number(b.machine_display));

        if (boxMappings.length === 0) return <span style={{ color: '#475569', fontStyle: 'italic' }}>No machines linked</span>;

        return (
          <Space wrap size={[8, 8]}>
            {boxMappings.map(m => (
              <div key={m.id} style={{ background: '#0f172a', padding: '6px 12px', borderRadius: '4px', border: '1px solid #334155', color: '#3b82f6', fontWeight: '500', fontSize: '13px', display: 'flex', alignItems: 'center' }}>
                <ClusterOutlined style={{ marginRight: '8px', fontSize: '12px', color: '#64748b' }} />
                <span>{m.machine_display}</span>
                <span style={{ color: '#94a3b8', marginLeft: '4px' }}>({m.left_field},{m.right_field})</span>
              </div>
            ))}
          </Space>
        );
      }
    },
    {
      title: 'ACTIONS',
      key: 'actions',
      width: 250, // Povećao širinu zbog novog dugmeta
      render: (record) => {
        const isActive = activeMappings[record.box_id];
        return (
          <Space size="small">
            {/* NOVO DUGME ZA MAPPING */}
            <Button 
              icon={<EyeOutlined />} 
              size="small" 
              onClick={() => toggleMapping(record)}
              style={{ 
                background: isActive ? '#059669' : '#1e293b', 
                color: isActive ? 'white' : '#94a3b8', 
                border: isActive ? '1px solid #10b981' : '1px solid #334155',
                fontWeight: isActive ? 'bold' : 'normal'
              }}
            >
              {isActive ? 'Mapping Hide' : 'Mapping Show'}
            </Button>

            <Button 
              icon={<EditOutlined />} 
              size="small" 
              style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}
              onClick={() => openFullEditModal(record)}
            >Edit</Button>
            
            <Popconfirm title="Delete all?" onConfirm={() => handleDeleteAllForBox(record.id)} okText="Yes" cancelText="No">
              <Button icon={<DeleteOutlined />} size="small" style={{ background: '#1e293b', color: '#94a3b8', border: '1px solid #334155' }}>Delete</Button>
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  const mappedBoxesCount = [...new Set(mappings.map(m => m.box))].length;

  return (
    <div style={{ padding: '0px' }}>
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }} align="stretch">
        <Col span={8}>
          <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', minHeight: CARD_MIN_HEIGHT, display: 'flex', alignItems: 'center', height: '100%' }}>
            <Space size="middle">
              <BoxPlotOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>TOTAL BOXES</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{boxes.length}</div>
              </div>
            </Space>
          </div>
        </Col>
        <Col span={8}>
          <div style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', minHeight: CARD_MIN_HEIGHT, display: 'flex', alignItems: 'center', height: '100%' }}>
            <Space size="middle">
              <DeploymentUnitOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#64748b', fontSize: '12px' }}>MAPPED BOXES</div>
                <div style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>{mappedBoxesCount}</div>
              </div>
            </Space>
          </div>
        </Col>
        <Col span={8}>
          <div onClick={openAddModal} style={{ background: '#1e293b', padding: CARD_PADDING, borderRadius: '8px', border: '1px solid #334155', borderBottom: '3px solid #3b82f6', cursor: 'pointer', minHeight: CARD_MIN_HEIGHT, display: 'flex', alignItems: 'center', height: '100%' }}>
            <Space size="middle">
              <PlusOutlined style={{ color: '#3b82f6', fontSize: '24px' }} />
              <div>
                <div style={{ color: '#3b82f6', fontSize: '12px', fontWeight: 'bold' }}>ADD NEW MAPPING</div>
                <div style={{ color: 'white', fontSize: '14px' }}>Configure link</div>
              </div>
            </Space>
          </div>
        </Col>
      </Row>

      <div style={{ background: 'rgba(30, 41, 59, 0.3)', padding: '24px', borderRadius: '12px', border: '1px solid #1e293b' }}>
        <Table dataSource={boxes} columns={columns} rowKey="id" loading={loading} pagination={false} style={{ background: 'transparent' }} />
      </div>

      <EditBoxModal title={editingBoxId ? "Edit Box Mapping" : "Create New Mappings"} isVisible={isModalVisible} onOk={() => form.submit()} onCancel={() => setIsModalVisible(false)} width={800}>
        <Form form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="box" label={<span style={{ color: '#94a3b8' }}>TARGET BOX</span>} rules={[{ required: true }]}>
            <Select dropdownStyle={{ background: '#1e293b' }} placeholder="Select target box">
              {boxes.map(b => <Option key={b.id} value={b.id}>{b.box_name || b.box_id}</Option>)}
            </Select>
          </Form.Item>

          <Form.List name="rows">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <Row gutter={10} key={key} align="bottom" style={{ marginBottom: '10px' }}>
                    <Col span={9}>
                      <Form.Item {...restField} name={[name, 'machine']} label={index === 0 ? <span style={{ color: '#94a3b8' }}>MACHINE</span> : null} rules={[{ required: true, message: '' }]}>
                        <Select dropdownStyle={{ background: '#1e293b' }} placeholder="Select">
                          {machines.map(m => (
                            <Option key={m.id} value={m.machine_id}>{m.machine_id} ({m.name})</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'left_field']} label={index === 0 ? <span style={{ color: '#94a3b8' }}>LEFT</span> : null} rules={[{ required: true, message: '' }]}>
                        <Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} placeholder="ID" />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'right_field']} label={index === 0 ? <span style={{ color: '#94a3b8' }}>RIGHT</span> : null} rules={[{ required: true, message: '' }]}>
                        <Input style={{ background: '#0f172a', color: 'white', border: '1px solid #334155' }} placeholder="ID" />
                      </Form.Item>
                    </Col>
                    <Col span={3}>
                      <Button type="text" danger onClick={() => remove(name)} icon={<MinusCircleOutlined />} style={{ marginBottom: '24px' }} />
                    </Col>
                  </Row>
                ))}
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} style={{ color: '#94a3b8', borderStyle: 'dashed', background: 'transparent', marginTop: '10px' }}>
                  Add another machine
                </Button>
              </>
            )}
          </Form.List>
        </Form>
      </EditBoxModal>

      <style>{`
        .ant-table { background: transparent !important; color: #94a3b8 !important; }
        .ant-table-thead > tr > th { background: transparent !important; color: #64748b !important; border-bottom: 1px solid #1e293b !important; text-transform: uppercase; font-size: 11px; }
        .ant-table-tbody > tr > td { border-bottom: 1px solid #1e293b !important; }
        .ant-table-tbody > tr:hover > td { background: rgba(255,255,255,0.02) !important; }
        .ant-select-selector, .ant-input { background: #0f172a !important; color: white !important; border: 1px solid #334155 !important; }
        .ant-select-item { color: #94a3b8 !important; }
        .ant-select-item-option-active { background: #334155 !important; color: white !important; }
        .ant-select-item-option-selected { background: #3b82f6 !important; color: white !important; }
        
        /* FIX ZA PADANJE MENIJA IZA MODALA */
        .ant-select-dropdown {
          z-index: 10001 !important;
        }
      `}</style>
    </div>
  );
};

export default MachineMappings;